import React from 'react';
import { useTranslation } from '../../i18n/i18n';
import { ApiOrderReturn } from '../../types';
import { ContentCard } from '../ContentCard/ContentCard';
import { OrderReturnItem } from './OrderReturnItem';
import './OrderReturnList.css';

interface OrderReturnListProps {
	orderReturns: ApiOrderReturn[] | undefined;
}

export const OrderReturnList: React.FC<OrderReturnListProps> = ({ orderReturns }) => {
	if (!orderReturns || !orderReturns || orderReturns.length === 0) {
		return null;
	}

	const t = useTranslation().returnOrder;

	const formatDate = (timestamp: number): string => {
		return new Date(timestamp).toLocaleDateString();
	};

	const getTypeText = (type: string): string => {
		switch (type) {
			case 'return':
				return t.return;
			case 'reclamation':
				return t.reclamation;
			default:
				return type.charAt(0).toUpperCase() + type.slice(1);
		}
	};

	return (
		<div className="return-orders-list">
			{orderReturns.map((orderReturn) => (
				<ContentCard key={orderReturn.returnId} className="return-order-card">
					<div className="return-order-header">
						<h3>{getTypeText(orderReturn.type)}</h3>
						<span className="return-date">
							{t.created} {formatDate(orderReturn.dateCreated)}
						</span>
					</div>

					<div className="return-order-details">
						<div className="return-reason">
							<strong>{t.reason}:</strong> {orderReturn.reason}
						</div>
						{orderReturn.description && (
							<div className="return-description">
								<strong>{t.description}:</strong> {orderReturn.description}
							</div>
						)}
					</div>

					{orderReturn.items.length > 0 && (
						<div className="return-items">
							<h4>{t.products}:</h4>
							<ul className="selected-products-list">
								{orderReturn.items.map((item) => (
									<li key={item.id} className="selected-product-item">
										<OrderReturnItem
											product={item.product}
											returnQuantity={item.returnQuantity}
											productCondition={item.productCondition}
										/>
									</li>
								))}
							</ul>
						</div>
					)}

					{orderReturn.images.length > 0 && (
						<div className="return-images">
							<h4>{t.images}:</h4>
							<div className="return-images-grid">
								{orderReturn.images.map((image, index) => (
									<a key={index} href={image.url} target="_blank" rel="noopener noreferrer">
										<img
											key={index}
											src={image.urlPreview}
											alt={`Return evidence ${index + 1}`}
											className="return-image"
										/>
									</a>
								))}
							</div>
						</div>
					)}
				</ContentCard>
			))}
		</div>
	);
};
